import {
  getFontCss,
  HoverableLinkDesigns,
  HoverableLinkSizes,
  styleConstants,
  themeConstants,
} from '@paywithextend/components'
import { spacing } from 'ext-ui/style/spacing'
import React, { useState } from 'react'
import { useSelector } from 'react-redux'
import AnimatedTableChevron from 'src/components/AnimatedTableChevron/AnimatedTableChevron'
import { HomeActivityStat } from 'src/components/HomeActivityBox/HomeActivityBox'
import HoverableLink from 'src/components/HoverableLink/HoverableLink'
import LoadingShimmer from 'src/components/LoadingShimmer/LoadingShimmer'
import { selectBrandColorFontPrimary } from 'src/redux/selectors/session.selectors'
import { css } from 'styled-components'

export type CachedAlertStats = Record<string, number | undefined>

interface HomeStateLinkProps {
  stat: HomeActivityStat
  index: number
  isLast: boolean
  showLoadingState?: boolean
  cachedStats?: CachedAlertStats
  timedOut?: boolean
  loadingState?: boolean
}

const HomeStatLink: React.FC<HomeStateLinkProps> = (
  props,
): React.ReactElement => {
  const brandColorFontPrimary = useSelector(selectBrandColorFontPrimary)
  const statNumber =
    props.stat.statNumber ?? Object.values(props.cachedStats || {})[props.index]
  const [hovered, setHovered] = useState(false)

  const loadingElement = (
    <div
      css={css`
        display: flex;
        align-items: center;
        justify-content: space-between;
      `}
    >
      <LoadingShimmer width={'250px'} />
      <span
        css={css`
          display: flex;
          align-items: center;
        `}
      >
        <LoadingShimmer width={'20px'} />
        <AnimatedTableChevron
          css={css`
            transform: scale(calc(2 / 3));
            transform-origin: center;
            margin-bottom: 2px;
          `}
          fill={brandColorFontPrimary}
        />
      </span>
    </div>
  )

  return (
    <div
      key={props.index}
      css={css`
        ${!props.isLast &&
        css`
          margin-bottom: 14px;
        `}
      `}
      onMouseEnter={(): void => setHovered(true)}
      onMouseLeave={(): void => setHovered(false)}
      onFocus={(): void => setHovered(true)}
    >
      {props.showLoadingState &&
      ((!props.timedOut && props.loadingState) || statNumber === undefined) ? (
        loadingElement
      ) : (
        <HoverableLink
          css={css`
            display: flex;
            align-items: center;
            gap: ${spacing(1)};
            ${props.stat.customCss}
          `}
          to={props.stat.to}
          onClick={props.stat.onClick}
          design={HoverableLinkDesigns.Primary}
          size={HoverableLinkSizes.Medium}
          regular
          ariaLabelSource={props.stat.ariaLabelSource}
        >
          <div
            css={css`
              /* TODO: (2025-08-04) remove when SVGs are fixed */
              /* the svg has 4px of padding at the bottom, so balance it */
              padding-top: 4px;
              /* force the div to be the right size (20px icon + 8px padding) */
              height: 28px;
            `}
          >
            {hovered ? props.stat.icons.hoverIcon : props.stat.icons.staticIcon}
          </div>
          <div
            css={css`
              flex: auto;
              /* By default flex items can't be smaller than 'min-content' */
              /* Allows the div to shrink smaller than the contents if overflowing */
              min-width: 0;
            `}
          >
            <div
              css={css`
                /* For the tooltip */
                position: relative;

                /* Don't allow width to grow past the content size */
                display: inline-block;
                /* It's an inline box, so height won't fill container */
                vertical-align: middle;
                /* Don't allow overflowing container */
                max-width: 100%;
              `}
              onMouseEnter={(): void => {
                props.stat.tooltipProps?.tooltipFunction(true)
                props.stat.tooltipProps?.tooltipAnalytics()
              }}
              onMouseLeave={(): void =>
                props.stat.tooltipProps?.tooltipFunction(false)
              }
            >
              <span
                css={css`
                  ${getFontCss(
                    14,
                    themeConstants.colors.font_primary,
                    styleConstants.fontFamilies.medium,
                  )};

                  /* So it won't overflow it's container */
                  display: block;
                  overflow: hidden;
                  text-overflow: ellipsis;
                `}
              >
                {props.stat.text}
              </span>
              {props.stat.tooltipProps?.tooltip}
            </div>
          </div>
          <span
            css={css`
              display: flex;
              align-items: center;
            `}
          >
            {statNumber !== undefined && (
              <span
                data-testid={props.stat.testId}
                className="stat"
                css={css`
                  ${statNumber > 0 &&
                  css`
                    ${getFontCss(
                      14,
                      styleConstants.colors.red,
                      styleConstants.fontFamilies.regular,
                    )}
                  `};
                `}
              >
                {statNumber >= 1000 ? '999+' : statNumber.toString()}
              </span>
            )}
            {statNumber === undefined && '--'}
            <AnimatedTableChevron
              css={css`
                transform: scale(calc(2 / 3));
                transform-origin: center;
                margin-bottom: 2px;
              `}
            />
          </span>
        </HoverableLink>
      )}
    </div>
  )
}

export default HomeStatLink
